import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  ListItemIcon,
  TextField,
  InputAdornment,
  Collapse,
  ListItemButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Avatar,
} from '@mui/material';
import { usePlanning } from '../context/PlanningContext';
import { useAuth } from '../context/AuthContext';
import type { Risorsa, RisorsaInput } from '../types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import ResourceForm from './ResourceForm';
import * as api from '../services/api';

interface ResourceItemProps {
  risorsa: Risorsa;
  isSelected: boolean;
  isComplete: boolean;
  hasAllocations: boolean;
  onClick: () => void;
  onEdit: (risorsa: Risorsa) => void;
  onDelete: (risorsa: Risorsa) => void;
  canUpdate: boolean;
  canDelete: boolean;
}

const ResourceItem: React.FC<ResourceItemProps> = ({
  risorsa,
  isSelected,
  isComplete,
  hasAllocations,
  onClick,
  onEdit,
  onDelete,
  canUpdate,
  canDelete,
}) => {
  const [hover, setHover] = useState(false);

  const getStatusIcon = () => {
    if (isComplete) return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    if (hasAllocations) return <WarningIcon sx={{ color: 'warning.main' }} />;
    return <CircleOutlinedIcon sx={{ color: 'text.disabled' }} />;
  };

  const showActions = hover && (canUpdate || canDelete);

  return (
    <ListItem
      button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        mb: 0.5,
        borderRadius: 1,
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        cursor: 'pointer',
        pl: 4,
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'primary.main',
        },
      }}
    >
      <Avatar
        src={risorsa.foto || undefined}
        sx={{ width: 40, height: 40, mr: 2 }}
      >
        {!risorsa.foto && <PersonIcon />}
      </Avatar>
      <ListItemIcon sx={{ minWidth: 40 }}>
        {getStatusIcon()}
      </ListItemIcon>
      <ListItemText
        primary={`${risorsa.nome}${risorsa.cognome ? ' ' + risorsa.cognome : ''}`}
        secondary={
          risorsa.skills && risorsa.skills.length > 0
            ? risorsa.skills.map(s => s.nome).join(', ')
            : risorsa.figura_professionale_nome || '-'
        }
        primaryTypographyProps={{ fontWeight: isSelected ? 'bold' : 'normal', fontSize: '0.9rem' }}
        secondaryTypographyProps={{ fontSize: '0.75rem' }}
      />
      {showActions && (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canUpdate && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(risorsa);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(risorsa);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </ListItem>
  );
};

const ResourceList: React.FC = () => {
  const { allocazioni, anno, setSelectedRisorsa, selectedRisorsa } = usePlanning();
  const { user, hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showDisabled, setShowDisabled] = useState<'enabled' | 'all'>('enabled');
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRisorsa, setEditingRisorsa] = useState<Risorsa | null>(null);

  // Load risorse
  const loadRisorse = async () => {
    try {
      const data = await api.getRisorse(showDisabled === 'all');

      // Filtra risorse in base alle strutture dell'utente
      let risorseFiltrate = data;
      if (user && !user.hasTutteLeStrutture) {
        risorseFiltrate = data.filter((r: Risorsa) =>
          user.strutture.includes(r.struttura || '')
        );
      }

      setRisorse(risorseFiltrate);
    } catch (error) {
      console.error('Error loading risorse:', error);
    }
  };

  useEffect(() => {
    loadRisorse();
  }, [showDisabled, user]);

  // Gestione CRUD
  const handleCreate = () => {
    setEditingRisorsa(null);
    setFormOpen(true);
  };

  const handleEdit = (risorsa: Risorsa) => {
    setEditingRisorsa(risorsa);
    setFormOpen(true);
  };

  const handleDelete = async (risorsa: Risorsa) => {
    if (!window.confirm(`Vuoi disabilitare la risorsa "${risorsa.nome} ${risorsa.cognome || ''}"?`)) {
      return;
    }

    try {
      await api.deleteRisorsa(risorsa.id);
      await loadRisorse();
      if (selectedRisorsa?.id === risorsa.id) {
        setSelectedRisorsa(null);
      }
    } catch (error) {
      console.error('Error deleting risorsa:', error);
      alert('Errore nella cancellazione della risorsa');
    }
  };

  const handleSave = async (risorsaInput: RisorsaInput, id?: number) => {
    try {
      if (id) {
        await api.updateRisorsa(id, risorsaInput);
      } else {
        await api.createRisorsa(risorsaInput);
      }
      await loadRisorse();
      setFormOpen(false);
      setEditingRisorsa(null);
    } catch (error) {
      console.error('Error saving risorsa:', error);
      alert('Errore nel salvataggio della risorsa');
    }
  };

  // Calcola mese corrente
  const oggi = new Date();
  const meseCorrente = oggi.getFullYear() === anno ? oggi.getMonth() + 1 : 1;
  const mesiDaVerificare = Array.from({ length: 13 - meseCorrente }, (_, i) => meseCorrente + i);

  // Calcola stato di ogni risorsa
  const getResourceStatus = (risorsaId: number) => {
    const allocazioniRisorsa = allocazioni.filter(
      (a) => a.id_risorsa === risorsaId && a.anno === anno
    );

    const hasAllocations = allocazioniRisorsa.length > 0;

    const isComplete = mesiDaVerificare.every((mese) => {
      const totale = allocazioniRisorsa
        .filter((a) => a.mese === mese)
        .reduce((sum, a) => sum + a.percentuale, 0);
      return totale === 100;
    });

    return { hasAllocations, isComplete };
  };

  // Filtra e raggruppa risorse per struttura
  const groupedResources = useMemo(() => {
    const filtered = risorse.filter((r) => {
      const skillNames = r.skills ? r.skills.map(s => s.nome).join(' ') : '';
      const fullName = `${r.nome} ${r.cognome || ''} ${r.struttura || ''} ${skillNames}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });

    const groups: Record<string, Risorsa[]> = {};
    filtered.forEach((risorsa) => {
      const struttura = risorsa.struttura || 'Altro';
      if (!groups[struttura]) {
        groups[struttura] = [];
      }
      groups[struttura].push(risorsa);
    });

    // Ordina risorse all'interno di ogni gruppo
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const nameA = `${a.nome} ${a.cognome || ''}`;
        const nameB = `${b.nome} ${b.cognome || ''}`;
        return nameA.localeCompare(nameB);
      });
    });

    return groups;
  }, [risorse, searchTerm]);

  // Calcola numero di progetti per struttura
  const getProjectCountForStruttura = (struttura: string) => {
    const risorseStruttura = groupedResources[struttura] || [];
    const idRisorseStruttura = risorseStruttura.map(r => r.id);

    // Trova tutti gli ID progetti allocati alle risorse di questa struttura
    const progettiAllocati = allocazioni
      .filter(a => idRisorseStruttura.includes(a.id_risorsa) && a.anno === anno)
      .map(a => a.id_progetto);

    // Conta i progetti UNICI usando Set (rimuove duplicati)
    const progettiUnici = new Set(progettiAllocati);
    return progettiUnici.size;
  };

  const toggleGroup = (struttura: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(struttura)) {
      newExpanded.delete(struttura);
    } else {
      newExpanded.add(struttura);
    }
    setExpandedGroups(newExpanded);
  };

  // Espandi tutti i gruppi all'inizio o quando c'è una ricerca
  useEffect(() => {
    if (searchTerm) {
      setExpandedGroups(new Set(Object.keys(groupedResources)));
    }
  }, [searchTerm, groupedResources]);

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Risorse</Typography>
        {hasPermission('risorse', 'create') && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nuova
          </Button>
        )}
      </Box>

      {/* Toggle Abilitati/Tutti */}
      <ToggleButtonGroup
        value={showDisabled}
        exclusive
        onChange={(_, value) => value && setShowDisabled(value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="enabled">Solo Abilitati</ToggleButton>
        <ToggleButton value="all">Tutti</ToggleButton>
      </ToggleButtonGroup>

      {/* Casella di ricerca */}
      <TextField
        size="small"
        placeholder="Cerca risorse..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Clicca per selezionare una risorsa
      </Typography>

      {/* Lista raggruppata */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {Object.keys(groupedResources).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Nessuna risorsa trovata
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {Object.keys(groupedResources)
              .sort()
              .map((struttura) => (
                <Box key={struttura}>
                  {/* Header gruppo */}
                  <ListItemButton
                    onClick={() => toggleGroup(struttura)}
                    sx={{
                      bgcolor: 'action.hover',
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <ListItemText
                      primary={struttura}
                      secondary={`${groupedResources[struttura].length} risorse - ${getProjectCountForStruttura(struttura)} progetti`}
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                    {expandedGroups.has(struttura) ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  {/* Risorse del gruppo */}
                  <Collapse in={expandedGroups.has(struttura)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {groupedResources[struttura].map((risorsa) => {
                        const { hasAllocations, isComplete } = getResourceStatus(risorsa.id);
                        return (
                          <ResourceItem
                            key={risorsa.id}
                            risorsa={risorsa}
                            isSelected={selectedRisorsa?.id === risorsa.id}
                            isComplete={isComplete}
                            hasAllocations={hasAllocations}
                            onClick={() => setSelectedRisorsa(risorsa)}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            canUpdate={hasPermission('risorse', 'update')}
                            canDelete={hasPermission('risorse', 'delete')}
                          />
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              ))}
          </List>
        )}
      </Box>

      {/* Form Dialog */}
      <ResourceForm
        open={formOpen}
        risorsa={editingRisorsa}
        onClose={() => {
          setFormOpen(false);
          setEditingRisorsa(null);
        }}
        onSave={handleSave}
      />
    </Paper>
  );
};

export default ResourceList;
